import { createSelector } from 'reselect';

import { selectors as orderSelectors } from 'state/order';
import { isEmpty } from 'utils/types';

const getState = (state) => state.editLog;

const getEditLog = createSelector(getState, (editLog) => editLog);

const getActionType = ({ resource, commandType, commandSubType }) => {
  const resourceType = {
    shipments: 'SHIPMENT',
    shipment: 'SHIPMENT',
    loads: 'LOAD',
    load: 'LOAD',
    order: 'ORDER',
  };
  const commandTypeKey = {
    CHANGE: 'EDIT',
    setExtra$: 'EDIT',
    SET: 'EDIT',
    ADD: 'ADD',
    REMOVE: 'REMOVE',
  };

  const resourceTarget = resourceType[resource] || false;
  const command =
    commandTypeKey[commandSubType] || commandTypeKey[commandType] || false;

  const actionType =
    resourceTarget && command ? `${command}_${resourceTarget}` : false;
  return { action: actionType, resource: resourceTarget };
};

const getResourceId = ({ resource, log }) => {
  const keyByResource = {
    shipments: 'shipmentKey',
    shipment: 'shipmentKey',
    loads: 'loadKey',
    load: 'loadKey',
  };

  const key = keyByResource[resource] || false;
  if (key) {
    return log && log.payload && log.payload[key];
  }
  return false;
};

const commandCreator = ({ log, shipmentsForBackend, orderExtras, orderId }) => {
  const [resourceKey, commandType] = log.type.split('/');

  if (!resourceKey || !commandType) {
    return false;
  }

  const [commandSubType] = commandType.split('_');

  if (!commandSubType) {
    return false;
  }

  const { action, resource } = getActionType({
    resource: resourceKey,
    commandType,
    commandSubType,
  });

  let id = getResourceId({ resource: resourceKey, log });
  let payload = {};
  if (log && log.payload && log.payload.values) {
    payload = log.payload.values;
  } else {
    if (resource === 'SHIPMENT') {
      const shipment = shipmentsForBackend[String(id)];
      if (!isEmpty(shipment)) {
        if (commandType === 'SET_COMMENT_FOR_TRANSPORTIST') {
          payload = {
            pickup_comment_from_shipper_to_driver:
              shipment.pickup_comment_from_shipper_to_driver,
            dropoff_comment_from_shipper_to_driver:
              shipment.dropoff_comment_from_shipper_to_driver,
          };
        }

        if (commandType === 'SET_INTERNAL_REFERENCE') {
          payload = {
            internal_reference: shipment.internal_reference,
          };
        }

        if (commandType === 'SET_EXTRA') {
          payload = {
            extras_list: shipment.extras,
          };
        }
      }
    }

    if (resource === 'ORDER') {
      payload = {
        extras_list: [...new Set(orderExtras)],
      };

      id = orderId;
    }
  }

  if (action && id && payload) {
    return { action, id: String(id), payload };
  }
  return false;
};

const getEditLogForBackend = createSelector(
  getEditLog,
  orderSelectors.getShipmentsForCreateOrder,
  orderSelectors.getExtras,
  orderSelectors.getOriginalId,
  (logs, shipmentsForBackend, orderExtras, orderId) => {
    try {
      let commandsById = {};

      logs.forEach((log) => {
        try {
          const command = commandCreator({
            log,
            shipmentsForBackend,
            orderExtras,
            orderId,
          });
          if (command && command.id) {
            commandsById = {
              ...commandsById,
              [command.id]: {
                ...command,
                payload: {
                  ...((commandsById[command.id] &&
                    commandsById[command.id].payload) ||
                    {}),
                  ...command.payload,
                },
              },
            };
          }
        } catch (e) {
          console.log(e);
        }
      });

      commandsById = {
        ...commandsById,
        [orderId]: commandsById[orderId]
          ? {
              ...commandsById[orderId],
              payload: {
                ...(commandsById[orderId].payload || {}),
                extras_list: orderExtras,
              },
            }
          : {
              action: 'EDIT_ORDER',
              id: String(orderId),
              payload: {
                extras_list: [...new Set(orderExtras)],
              },
            },
      };
      const commandsArray = Object.values(commandsById || {}) || [];

      const commandsPreparedForBackend = (commandsArray || []).map(
        (command) => {
          if (command.action === 'EDIT_SHIPMENT' && command.payload) {
            return {
              ...(command || {}),
              payload: {
                ...orderSelectors.prepareShipmentWithTimesForBackend({
                  shipment: command.payload,
                }),
              },
            };
          }
          return command;
        }
      );

      return commandsPreparedForBackend || [];
    } catch (e) {
      console.log('COMMAND ERROR:', e);
      return [];
    }
  }
);

export default {
  getEditLog,
  getEditLogForBackend,
};
